import { useState, useEffect, useMemo } from "react";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";
import type { Connector } from "./Dashboard";

interface Schedule {
  id:              string;
  connector_id:    string;
  connector_name:  string;
  cron_expression: string;
  data_types:      string[];
  retention_days:  number;
  is_active:       boolean;
  created_at:      string;
  updated_at:      string;
}

// Each data type declares the connector types it applies to so the UI can
// filter the checkbox list based on which connector the schedule is for.
type ConnectorKind = "freeagent" | "xero";
interface DataTypeDef {
  id:         string;
  label:      string;
  group:      string;
  connectors: ConnectorKind[];
}

const ALL_DATA_TYPES: DataTypeDef[] = [
  // ── Shared (FreeAgent + Xero) ─────────────────────────────────────────────
  { id: "invoices",                  label: "Invoices",                  group: "Financial",        connectors: ["freeagent", "xero"] },
  { id: "bills",                     label: "Bills",                     group: "Financial",        connectors: ["freeagent", "xero"] },
  { id: "credit_notes",              label: "Credit notes",              group: "Financial",        connectors: ["freeagent", "xero"] },
  { id: "contacts",                  label: "Contacts",                  group: "Financial",        connectors: ["freeagent", "xero"] },
  { id: "bank_transactions",         label: "Bank transactions",         group: "Banking",          connectors: ["freeagent", "xero"] },
  { id: "reports",                   label: "Reports",                   group: "Accounting",       connectors: ["freeagent", "xero"] },

  // ── FreeAgent-only ────────────────────────────────────────────────────────
  { id: "invoice_pdfs",              label: "Invoice PDFs",              group: "Financial",        connectors: ["freeagent"] },
  { id: "expenses",                  label: "Expenses",                  group: "Financial",        connectors: ["freeagent"] },
  { id: "estimates",                 label: "Estimates",                 group: "Financial",        connectors: ["freeagent"] },
  { id: "attachments",               label: "Attachments",               group: "Financial",        connectors: ["freeagent"] },
  { id: "projects",                  label: "Projects",                  group: "Projects & Time",  connectors: ["freeagent"] },
  { id: "tasks",                     label: "Tasks",                     group: "Projects & Time",  connectors: ["freeagent"] },
  { id: "timeslips",                 label: "Timeslips",                 group: "Projects & Time",  connectors: ["freeagent"] },
  { id: "price_list_items",          label: "Price list items",          group: "Projects & Time",  connectors: ["freeagent"] },
  { id: "vat_returns",               label: "VAT returns",               group: "Tax",              connectors: ["freeagent"] },
  { id: "corporation_tax_returns",   label: "Corporation tax returns",   group: "Tax",              connectors: ["freeagent"] },
  { id: "income_tax_returns",        label: "Income tax returns",        group: "Tax",              connectors: ["freeagent"] },
  { id: "final_accounts_reports",    label: "Final accounts reports",    group: "Tax",              connectors: ["freeagent"] },
  { id: "year_ends",                 label: "Year ends",                 group: "Tax",              connectors: ["freeagent"] },
  { id: "payroll",                   label: "Payroll",                   group: "Payroll & HR",     connectors: ["freeagent"] },
  { id: "users",                     label: "Users",                     group: "Payroll & HR",     connectors: ["freeagent"] },
  { id: "capital_assets",            label: "Capital assets",            group: "Assets",           connectors: ["freeagent"] },
  { id: "hire_purchases",            label: "Hire purchases",            group: "Assets",           connectors: ["freeagent"] },
  { id: "journal_entries",           label: "Journal entries",           group: "Accounting",       connectors: ["freeagent"] },
  { id: "categories",                label: "Categories",                group: "Accounting",       connectors: ["freeagent"] },
  { id: "company",                   label: "Company",                   group: "Company",          connectors: ["freeagent"] },
  { id: "sender_email_addresses",    label: "Sender email addresses",    group: "Company",          connectors: ["freeagent"] },

  // ── Xero-only ─────────────────────────────────────────────────────────────
  { id: "manual_journals",           label: "Manual journals",           group: "Accounting",       connectors: ["xero"] },
  // journals (GL detail) removed — requires accounting.journals.read, which is
  // an uplifted scope that needs per-app approval from Xero. Re-enable once
  // the scope is granted on the app in the developer portal.
  { id: "accounts",                  label: "Chart of accounts",         group: "Accounting",       connectors: ["xero"] },
  { id: "tax_rates",                 label: "Tax rates",                 group: "Accounting",       connectors: ["xero"] },
  { id: "tracking_categories",       label: "Tracking categories",       group: "Accounting",       connectors: ["xero"] },
  { id: "budgets",                   label: "Budgets",                   group: "Accounting",       connectors: ["xero"] },
  { id: "purchase_orders",           label: "Purchase orders",           group: "Financial",        connectors: ["xero"] },
  { id: "quotes",                    label: "Quotes",                    group: "Financial",        connectors: ["xero"] },
  { id: "payments",                  label: "Payments",                  group: "Financial",        connectors: ["xero"] },
  { id: "overpayments",              label: "Overpayments",              group: "Financial",        connectors: ["xero"] },
  { id: "prepayments",               label: "Prepayments",               group: "Financial",        connectors: ["xero"] },
  { id: "repeating_invoices",        label: "Repeating invoices",        group: "Financial",        connectors: ["xero"] },
  // expense_claims and receipts removed — they live under the deprecated
  // accounting.transactions.read broad scope which isn't available on apps
  // created after 2 Mar 2026. Users on Xero Expenses should backup via that
  // separate API when we add a Xero Expenses connector.
  { id: "items",                     label: "Items / products",          group: "Financial",        connectors: ["xero"] },
  { id: "bank_transfers",            label: "Bank transfers",            group: "Banking",          connectors: ["xero"] },
  { id: "contact_groups",            label: "Contact groups",            group: "Financial",        connectors: ["xero"] },
  { id: "currencies",                label: "Currencies",                group: "Company",          connectors: ["xero"] },
  { id: "branding_themes",           label: "Branding themes",           group: "Company",          connectors: ["xero"] },
  { id: "organisation",              label: "Organisation",              group: "Company",          connectors: ["xero"] },
  { id: "xero_users",                label: "Xero users",                group: "Company",          connectors: ["xero"] },
];

function dataTypesFor(kind: ConnectorKind): DataTypeDef[] {
  return ALL_DATA_TYPES.filter(t => t.connectors.includes(kind));
}

function defaultsFor(kind: ConnectorKind): string[] {
  return dataTypesFor(kind).map(t => t.id);
}

const DATA_TYPE_GROUPS = Array.from(new Set(ALL_DATA_TYPES.map(t => t.group)));

const DAYS = [
  { short: "Mon", long: "Monday",    value: 1 },
  { short: "Tue", long: "Tuesday",   value: 2 },
  { short: "Wed", long: "Wednesday", value: 3 },
  { short: "Thu", long: "Thursday",  value: 4 },
  { short: "Fri", long: "Friday",    value: 5 },
  { short: "Sat", long: "Saturday",  value: 6 },
  { short: "Sun", long: "Sunday",    value: 0 },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: String(i).padStart(2, "0"),
}));

const MINUTES = [0, 15, 30, 45].map(m => ({
  value: m,
  label: String(m).padStart(2, "0"),
}));

function buildCron(days: number[], hour: number, minute: number): string {
  if (days.length === 0 || days.length === 7) return `${minute} ${hour} * * *`;
  return `${minute} ${hour} * * ${days.sort((a, b) => a - b).join(",")}`;
}

function parseCron(cron: string): { days: number[]; hour: number; minute: number } {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { days: [], hour: 2, minute: 0 };
  const [min, hr, , , dow] = parts;
  const hour   = parseInt(hr)  || 0;
  const minute = parseInt(min) || 0;
  const days   = dow === "*" ? [] : dow.split(",").map(Number).filter(n => !isNaN(n));
  return { days, hour, minute };
}

function describeSchedule(days: number[], hour: number, minute: number): string {
  const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  if (days.length === 0 || days.length === 7) return `Every day at ${time}`;
  if (days.length === 1) {
    const day = DAYS.find(d => d.value === days[0]);
    return `Every ${day?.long ?? "day"} at ${time}`;
  }
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 5 && sorted.every((d, i) => d === [1,2,3,4,5][i]))
    return `Weekdays (Mon–Fri) at ${time}`;
  const names = sorted.map(d => DAYS.find(day => day.value === d)?.short ?? "");
  return `Every ${names.join(", ")} at ${time}`;
}

function describeCron(cron: string): string {
  const { days, hour, minute } = parseCron(cron);
  return describeSchedule(days, hour, minute);
}

export default function SchedulesPage() {
  const { get, post } = useApi();
  const [schedules, setSchedules]     = useState<Schedule[]>([]);
  const [connectors, setConnectors]   = useState<Connector[]>([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");
  const [pageError, setPageError]     = useState<string | null>(null);

  const [connectorId, setConnectorId]     = useState("");
  const [selectedDays, setSelectedDays]   = useState<number[]>([]);
  const [hour, setHour]                   = useState(2);
  const [minute, setMinute]               = useState(0);
  const [dataTypes, setDataTypes]         = useState<string[]>([]);
  const [retentionDays, setRetentionDays] = useState(90);
  const [isActive, setIsActive]           = useState(true);

  // Which connector kind does the currently-selected connector belong to?
  const selectedKind: ConnectorKind = useMemo(() => {
    const c = connectors.find(c => c.id === connectorId);
    return (c?.connector_type === "xero" ? "xero" : "freeagent");
  }, [connectorId, connectors]);
  const availableTypes = useMemo(() => dataTypesFor(selectedKind), [selectedKind]);
  const availableTypeIds = useMemo(() => availableTypes.map(t => t.id), [availableTypes]);

  const cronExpression    = useMemo(() => buildCron(selectedDays, hour, minute), [selectedDays, hour, minute]);
  const scheduleDescription = useMemo(() => describeSchedule(selectedDays, hour, minute), [selectedDays, hour, minute]);

  useEffect(() => {
    Promise.all([
      get<{ schedules: Schedule[] }>("/api/schedules"),
      get<{ connectors: Connector[] }>("/api/connectors"),
    ]).then(([s, c]) => {
      setSchedules(s.schedules);
      setConnectors(c.connectors);
      if (c.connectors.length > 0) setConnectorId(c.connectors[0].id);
    }).catch(err => setPageError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get]);

  const openModal = (existing?: Schedule) => {
    if (existing) {
      setConnectorId(existing.connector_id);
      const parsed = parseCron(existing.cron_expression);
      setSelectedDays(parsed.days);
      setHour(parsed.hour);
      setMinute(parsed.minute);
      setDataTypes(existing.data_types);
      setRetentionDays(existing.retention_days);
      setIsActive(existing.is_active);
    } else {
      const first = connectors[0];
      const kind: ConnectorKind = first?.connector_type === "xero" ? "xero" : "freeagent";
      setConnectorId(first?.id ?? "");
      setSelectedDays([]);
      setHour(2);
      setMinute(0);
      setDataTypes(defaultsFor(kind));
      setRetentionDays(90);
      setIsActive(true);
    }
    setError("");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!connectorId) return;
    if (dataTypes.length === 0) { setError("Select at least one data type"); return; }
    setSaving(true); setError("");
    try {
      const res = await post<{ schedule: Schedule }>("/api/schedules", {
        connector_id:    connectorId,
        cron_expression: cronExpression,
        data_types:      dataTypes,
        retention_days:  retentionDays,
        is_active:       isActive,
      });
      setSchedules(prev => {
        const idx = prev.findIndex(s => s.connector_id === connectorId);
        if (idx >= 0) { const n = [...prev]; n[idx] = res.schedule; return n; }
        return [...prev, res.schedule];
      });
      setShowModal(false);
    } catch (e: any) {
      setError(e.message ?? "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (schedule: Schedule) => {
    try {
      const res = await post<{ schedule: Schedule }>("/api/schedules", {
        connector_id:    schedule.connector_id,
        cron_expression: schedule.cron_expression,
        data_types:      schedule.data_types,
        retention_days:  schedule.retention_days,
        is_active:       !schedule.is_active,
      });
      setSchedules(prev => prev.map(s => s.id === schedule.id ? res.schedule : s));
    } catch (e: any) {
      setError(e.message ?? "Failed to update schedule");
    }
  };

  const toggleDay       = (d: number) => setSelectedDays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);
  const toggleDataType  = (id: string) => setDataTypes(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Schedules"
          subtitle={`${schedules.filter(s => s.is_active).length} active`}
          action={connectors.length > 0 ? (
            <button className="btn btn-primary btn-sm" onClick={() => openModal()}>+ New schedule</button>
          ) : undefined}
        />
        <main className="page">
          {pageError && <PageError message={pageError} onRetry={() => window.location.reload()} />}

          {!pageError && (<>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              {error}
              <button onClick={() => setError("")} style={{ marginLeft: 12, background: "none", border: "none", cursor: "pointer", color: "inherit" }}>✕</button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>Loading…</div>
          ) : connectors.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>⬡</div>
              <p style={{ fontWeight: 500, color: "var(--text2)" }}>No connectors yet</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Add a connector before setting up a schedule</p>
            </div>
          ) : schedules.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>⬡</div>
              <p style={{ fontWeight: 500, color: "var(--text2)" }}>No schedules configured</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Set up an automatic backup below</p>
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => openModal()}>+ Create first schedule</button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {schedules.map(schedule => (
                <div key={schedule.id} className="connector-card">
                  <div className="connector-card-top">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="connector-card-name">{schedule.connector_name}</div>
                      <div className="connector-card-type">{describeCron(schedule.cron_expression)}</div>
                    </div>
                    <label className="toggle" title={schedule.is_active ? "Pause" : "Resume"}>
                      <input type="checkbox" checked={schedule.is_active} onChange={() => toggleActive(schedule)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "10px 0" }}>
                    {schedule.data_types.map(t => (
                      <span key={t} style={{
                        fontSize: 11, fontFamily: "var(--mono)",
                        background: "var(--surface2)", border: "1px solid var(--border)",
                        borderRadius: "var(--r)", padding: "2px 8px", color: "var(--text2)",
                      }}>{t}</span>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--text3)" }}>
                      Retain {schedule.retention_days} days · {schedule.is_active ? "Active" : "Paused"}
                    </span>
                    <button className="btn btn-ghost btn-sm" onClick={() => openModal(schedule)}>Edit</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </>)}
        </main>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-head">
              <div style={{ fontWeight: 500, fontSize: 15 }}>Configure backup schedule</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Connector */}
              <div className="form-group">
                <label className="form-label">Connector</label>
                <select className="form-input" value={connectorId} onChange={e => {
                  const newId = e.target.value;
                  setConnectorId(newId);
                  // When the user switches to a connector of a different kind,
                  // reset to that kind's defaults. Keep the current selection
                  // if the connector's kind hasn't changed.
                  const prev = connectors.find(c => c.id === connectorId);
                  const next = connectors.find(c => c.id === newId);
                  if (prev?.connector_type !== next?.connector_type) {
                    const nextKind: ConnectorKind = next?.connector_type === "xero" ? "xero" : "freeagent";
                    setDataTypes(defaultsFor(nextKind));
                  }
                }}>
                  {connectors.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
                </select>
              </div>

              {/* Days */}
              <div className="form-group">
                <label className="form-label">Run on</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {DAYS.map(day => (
                    <button key={day.value} type="button" onClick={() => toggleDay(day.value)} title={day.long}
                      style={{
                        flex: 1, padding: "8px 4px", borderRadius: "var(--r)",
                        border: `1px solid ${selectedDays.includes(day.value) ? "var(--teal)" : "var(--border2)"}`,
                        background: selectedDays.includes(day.value) ? "var(--teal-bg)" : "var(--surface)",
                        color: selectedDays.includes(day.value) ? "var(--teal)" : "var(--text3)",
                        fontSize: 12, fontWeight: 500, cursor: "pointer",
                      }}>{day.short}</button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
                  {selectedDays.length === 0 ? "No days selected — runs every day" : `${selectedDays.length} day${selectedDays.length !== 1 ? "s" : ""} selected`}
                </div>
              </div>

              {/* Time */}
              <div className="form-group">
                <label className="form-label">Time (UTC)</label>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <select className="form-input" value={hour} onChange={e => setHour(parseInt(e.target.value))} style={{ width: 80 }}>
                      {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                    </select>
                    <span style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", textAlign: "center", letterSpacing: "0.05em" }}>HOUR</span>
                  </div>
                  <span style={{ color: "var(--text3)", fontSize: 20, fontWeight: 300, marginTop: 6 }}>:</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <select className="form-input" value={minute} onChange={e => setMinute(parseInt(e.target.value))} style={{ width: 80 }}>
                      {MINUTES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <span style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", textAlign: "center", letterSpacing: "0.05em" }}>MIN</span>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div style={{
                background: "var(--teal-bg)", border: "1px solid var(--teal)",
                borderRadius: "var(--r)", padding: "10px 14px",
                fontSize: 13, color: "var(--teal)",
              }}>
                <span style={{ opacity: 0.7, fontSize: 11, fontFamily: "var(--mono)", marginRight: 8 }}>WILL RUN</span>
                {scheduleDescription}
              </div>

              {/* Data types */}
              <div className="form-group">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>Data types</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={() => setDataTypes(availableTypeIds)}
                      style={{ fontSize: 11, color: "var(--teal)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                      Select all
                    </button>
                    <button type="button" onClick={() => setDataTypes([])}
                      style={{ fontSize: 11, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                      Clear
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
                  {DATA_TYPE_GROUPS.map(group => {
                    const inGroup = availableTypes.filter(t => t.group === group);
                    if (inGroup.length === 0) return null;
                    return (
                      <div key={group}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text3)", marginBottom: 6 }}>{group}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                          {inGroup.map(t => (
                            <button key={t.id} type="button" onClick={() => toggleDataType(t.id)}
                              style={{
                                fontSize: 11, fontFamily: "var(--mono)", padding: "3px 9px", borderRadius: "var(--r)",
                                border: `1px solid ${dataTypes.includes(t.id) ? "var(--teal)" : "var(--border2)"}`,
                                background: dataTypes.includes(t.id) ? "var(--teal-bg)" : "var(--surface)",
                                color: dataTypes.includes(t.id) ? "var(--teal)" : "var(--text3)", cursor: "pointer",
                              }}>{t.label}</button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>
                  {dataTypes.length} of {availableTypes.length} types selected
                </div>
              </div>

              {/* Retention */}
              <div className="form-group">
                <label className="form-label">Retention period</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="number" className="form-input" value={retentionDays} min={7} max={3650}
                    onChange={e => setRetentionDays(parseInt(e.target.value) || 90)} style={{ width: 100 }} />
                  <span style={{ fontSize: 13, color: "var(--text2)" }}>days</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Jobs older than this are automatically removed</div>
              </div>

              {/* Active */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="form-label" style={{ marginBottom: 2 }}>Active</div>
                  <div style={{ fontSize: 12, color: "var(--text3)" }}>Schedule runs automatically</div>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
            </div>

            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save schedule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}